import {animate, state, style, transition, trigger} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {authLogout} from '@spout/web-global/actions';
import {SptFirestoreService} from '@spout/web-global/data-access';
import {AccountState, EmailPassword, PartialAuthAccountStates} from '@spout/web-global/models';
import {getIsLoggedIn, selectAccountState} from '@spout/web-global/selectors';
import {SvgLoaderService} from '@spout/web-global/utils';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AlertMessageContainerComponent} from '../alert-message/alert-message-container.component';
import {ALERT_MESSAGE_ID} from '../alert-message/alert-message-store.service';
import {animationDelay, animationDuration} from '../animations/animation.constants';
import {LOGIN_CONFIG} from './login.config';
import {LOGIN_KEY} from './login.model';
import {MyErrorStateMatcher} from './logn.validators';
import {
  UserCredential,
  User,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider
} from 'firebase/auth';

@Component({
  selector: 'spt-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showMessage', [
      state('false', style({opacity: 0})),
      state('true', style({opacity: 1})),
      transition('false => true', [animate(`${animationDuration} ${animationDelay} ease-out`)]),
      transition('true => false', [animate(`${animationDuration} ease-in`)])
    ])
  ]
})
export class LoginComponent {
  private loginStorageState: string | null;
  showProgress$: BehaviorSubject<boolean>;
  showUi$: BehaviorSubject<boolean>;
  forgotPassword = false;
  selected = new FormControl(0);
  matcher = new MyErrorStateMatcher();
  config = LOGIN_CONFIG;
  ALERT_MESSAGE_ID = ALERT_MESSAGE_ID;
  isLoggedIn$: Observable<boolean>;
  notLoggedIn$: Observable<boolean>;
  account$: Observable<AccountState>;

  @Input() signInWithPopup = true;
  @Output() authEmailUser: EventEmitter<User | null> = new EventEmitter();

  @HostBinding('class.login-component') bind = true;

  @ViewChild('alertMessageContainerComponent') alertMessageContainerComponent!: AlertMessageContainerComponent;
  @ViewChild('alertMessageContainerComponentForgotPassword')
  alertMessageContainerComponentForgotPassword!: AlertMessageContainerComponent;
  @ViewChild('alertMessageContainerComponentSignUp')
  alertMessageContainerComponentSignUp!: AlertMessageContainerComponent;

  constructor(
    private sptFirebase: SptFirestoreService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private svgLoader: SvgLoaderService,
    private store: Store<PartialAuthAccountStates>
  ) {
    this.showProgress$ = new BehaviorSubject<boolean>(false);
    this.loginStorageState = localStorage.getItem(LOGIN_KEY);
    this.showUi$ = new BehaviorSubject<boolean>(false);
    this.svgLoader.addSvgIconSet();
    this.isLoggedIn$ = this.store.pipe(select(getIsLoggedIn));
    this.notLoggedIn$ = this.isLoggedIn$.pipe(map((isLoggedIn: boolean) => !isLoggedIn));
    this.account$ = this.store.pipe(select(selectAccountState));
  }

  setTabIndex(index: number) {
    this.selected.setValue(index, {emitEvent: false});
  }

  enableForgotPassword() {
    this.forgotPassword = true;
    this.selected.setValue(2);
    this.cd.detectChanges();
  }

  signInWithGoogle() {
    // https://firebase.google.com/docs/auth/web/google-signin
    const googleAuthProvider = new GoogleAuthProvider();
    // googleAuthProvider.addScope('https://www.googleapis.com/auth/contacts.readonly');

    // https://developers.google.com/identity/protocols/oauth2/openid-connect#authenticationuriparameters
    googleAuthProvider.setCustomParameters({
      prompt: 'select_account'
    });

    if (this.signInWithPopup) {
      this.sptFirebase.signInWithPopup(googleAuthProvider).then((userCredential: UserCredential) => {
        // console.log(userCredential);
        this.authEmailUser.emit(userCredential.user);
      });
    } else {
      this.sptFirebase.signInWithRedirect(googleAuthProvider).then(() => {
        /* noop */
      });
    }
  }

  signInWithFacebook() {
    const facebookAuthProvider = new FacebookAuthProvider();

    if (this.signInWithPopup) {
      this.sptFirebase.signInWithPopup(facebookAuthProvider).then((userCredential: UserCredential) => {
        // console.log(userCredential);
        this.authEmailUser.emit(userCredential.user);
      });
    } else {
      this.sptFirebase.signInWithRedirect(facebookAuthProvider).then(() => {
        /* noop */
      });
    }
  }

  signInWithTwitter() {
    const twitterAuthProvider = new TwitterAuthProvider();

    if (this.signInWithPopup) {
      this.sptFirebase.signInWithPopup(twitterAuthProvider).then((userCredential: UserCredential) => {
        // console.log(userCredential);
        this.authEmailUser.emit(userCredential.user);
      });
    } else {
      this.sptFirebase.signInWithRedirect(twitterAuthProvider).then(() => {
        /* noop */
      });
    }
  }

  signInWithEmail(c: EmailPassword) {
    this.sptFirebase
      .signInWithEmailAndPassword(c.email, c.password)
      .then((userCredential: UserCredential) => {
        // console.log(userCredential);
        this.authEmailUser.emit(userCredential.user);
      })
      .catch(error => {
        if (error.message && error.message.length) {
          this.alertMessageContainerComponent.addWarnMessage({
            id: ALERT_MESSAGE_ID.PASSWORD_AUTH,
            message: error.message
          });
          // this.cd.detectChanges();
        }
      });
  }

  createUserWithEmailAndPassword(c: EmailPassword) {
    this.sptFirebase
      .createUserWithEmailAndPassword(c.email, c.password)
      .then((userCredential: UserCredential) => {
        // console.log(userCredential);
        this.authEmailUser.emit(userCredential.user);
      })
      .catch((error: any) => {
        this.alertMessageContainerComponentSignUp.addWarnMessage({
          id: ALERT_MESSAGE_ID.CREATE_PASSWORD,
          message: error.message
        });
      });
  }

  sendEmailPasswordReset(email: string) {
    this.sptFirebase
      .sendPasswordResetEmail(email)
      .then(() => {
        // Email sent.
        this.alertMessageContainerComponentForgotPassword.addSuccessMessage({
          id: ALERT_MESSAGE_ID.RESET_EMAIL_SENT,
          message: 'Check your email for reset instructions.'
        });
      })
      .catch(error => {
        this.alertMessageContainerComponentForgotPassword.addWarnMessage({
          id: ALERT_MESSAGE_ID.EMAIL_RESET_PASSWORD,
          message: error.message
        });
      });
  }

  logout() {
    this.store.dispatch(authLogout());
  }
}
